import { Firebase } from "@/app/core/base/FirebaseApp";
import { toast } from "react-toastify";
import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  Timestamp,
  getCountFromServer,
  query,
  where,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import { ErrorHelper } from "../../util/helpers/ErrorHelper";
import { FireHelper } from "../../util/helpers/FireHelper";
import { DateHelper } from "../../util/helpers/DateHelper";
import algoliasearch from "algoliasearch";
import { ComplainHelper } from "../../util/helpers/ComplainHelper";
import Storage from "../../util/Storage";

export const ComplainController = {
  db: getFirestore(Firebase),

  ToFirestoreComplain: function (data) {
    let updatedData = {
      id: data.id,
      oldId: data.oldId,
      userId: data.userId,
      title: data.title,
      description: data.description,
      city: data.city,
      neighborhood: data.neighborhood,
      companyId: data.companyId,
      phone: data.phone,
      images: [...(data.images || [])],
      selectedServices: [...(data.selectedServices || [])],
      status: data.status ?? ComplainHelper.Status.Pending,
      transactionId: data.transactionId,
      transactionStatus: data.transactionStatus,
      createdDate: data.createdDate ?? Timestamp.now(),
      updatedDate: Timestamp.now(),
    };
    return FireHelper.ToFirestoreDoc(updatedData);
  },

  Insert: async function (data) {
    try {
      const coll = collection(this.db, "complains");
      const result = await addDoc(coll, this.ToFirestoreComplain(data));
      return result.id;
    } catch (e) {
      console.log("ComplainController insert error", e);
      toast.error(ErrorHelper.HandleMessage(e));
      return null;
    }
  },

  Update: async function (data) {
    try {
      const docRef = doc(this.db, "complains", data.id);
      await updateDoc(docRef, this.ToFirestoreComplain(data));
      return docRef.id;
    } catch (e) {
      console.log("ComplainController: ", e);
      toast.error(ErrorHelper.HandleMessage(e));
      return null;
    }
  },

  Get: async function (id, res) {
    const docRef = doc(this.db, "complains", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      res({ ...docSnap.data(), id: id });
    } else {
      res(null);
    }
  },

  GetOrder: async function (id, res) {
    const docRef = doc(this.db, "orders", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      res({ ...docSnap.data(), id: id });
    } else {
      res(null);
    }
  },

  List: async function (res) {
    const querySnapshot = await getDocs(collection(this.db, "complains"));
    const results = querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));
    res(results);
  },

  ListByUser: async function (uid, res) {
    const queryProperties = query(
      collection(this.db, "complains"),
      where("userId", "==", uid)
    );
    const querySnapshots = await getDocs(queryProperties);
    const results = querySnapshots.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));
    return results;
  },

  ListByCompany: async function (companyId) {
    const queryProperties = query(
      collection(this.db, "complains"),
      where("companyId", "==", companyId)
    );
    const querySnapshots = await getDocs(queryProperties);
    const results = querySnapshots.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));
    return results;
  },

  Query: async function ({ queryString, hitsPerPage }) {
    const searchClient = algoliasearch(
      "Y67ZPH9IGB",
      "a160a59d07299b8cdf05130b672ff01e"
    );

    let index = searchClient.initIndex("complains");
    const { hits } = await index.search(queryString, {
      hitsPerPage: hitsPerPage || 100,
    });

    return hits.map((doc) => ({ ...doc, id: doc.objectID }));
  },

  GetTotalCount: async function () {
    const coll = collection(this.db, "complains");
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  },

  GetReportsByDate: async function (startDate, endDate) {
    const complainsQuery = query(
      collection(this.db, "complains"),
      where("createdDate", ">=", Timestamp.fromDate(startDate)),
      where("createdDate", "<=", Timestamp.fromDate(endDate))
    );

    // Initialize a dictionary to hold counts for each month-year in the range
    const countsByMonth = {};

    // Generate all months between startDate and endDate
    let currentMonth = DateHelper.GetFirstDayOfMonthByDate(startDate);
    while (currentMonth <= endDate) {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const monthYear = `${year}-${month.toString().padStart(2, "0")}`;
      countsByMonth[monthYear] = {
        year: year,
        month: month,
        pendentes: 0,
        resolvidas: 0,
        respondidas: 0,
        nao_resolvidas: 0,
      };
      currentMonth = DateHelper.AddMonths(currentMonth, 1);
    }

    // Process the documents to group by month
    (await getDocs(complainsQuery)).forEach((doc) => {
      const createdDate = doc.data().createdDate.toDate();
      const year = createdDate.getFullYear();
      const month = createdDate.getMonth();
      const monthYear = `${year}-${month.toString().padStart(2, "0")}`;

      // Increment count for the corresponding month
      const currentValue = countsByMonth[monthYear];
      if (currentValue !== undefined) {
        if (doc.data().status === ComplainHelper.Status.Open) {
          currentValue.pendentes += 1;
        }
        if (doc.data().status === ComplainHelper.Status.Solved) {
          currentValue.resolvidas += 1;
        }
        if (doc.data().status === ComplainHelper.Status.Responded) {
          currentValue.respondidas += 1;
        }
        if (doc.data().status === ComplainHelper.Status.Unsolved) {
          currentValue.nao_resolvidas += 1;
        }
      }
    });

    // Convert countsByMonth to an array of objects
    const result = Object.keys(countsByMonth).map((key) => ({
      ...countsByMonth[key],
    }));

    return result;
  },

  ListByDate: async function (startDate, endDate) {
    const complainsQuery = query(
      collection(this.db, "complains"),
      where("createdDate", ">=", Timestamp.fromDate(startDate)),
      where("createdDate", "<=", Timestamp.fromDate(endDate))
    );

    // Process the documents to group by month
    const querySnapshots = await getDocs(complainsQuery);

    const results = querySnapshots.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));

    return results;
  },

  Delete: async function (id) {
    await deleteDoc(doc(this.db, "complains", id));
    return true;
  },
};
